import { type ComponentProps, type ReactNode } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/util/utils';

type EmptyStateProps = Omit<ComponentProps<'div'>, 'children' | 'title'> &
    VariantProps<typeof emptyStateVariants> & {
        /** A Lucide icon element; sized by the component. */
        icon: ReactNode;
        title: ReactNode;
        body?: ReactNode;
        actions?: ReactNode;
        /** The title's element. A heading when the empty state is the page's content, a paragraph inside a card. */
        heading?: 'p' | 'h1' | 'h2' | 'h3';
    };

/**
 * empty-state, READ geometry: a 48px circle on the recessed surface holding a 21px icon,
 * a text-sm-medium title, body copy capped at 400px, an optional actions row. `success` is
 * good news (nothing needs attention); `destructive` is a failure wearing the same shape,
 * with a smaller body and a wider measure, READ 210:3205.
 */
function EmptyState({
    icon,
    title,
    body,
    actions,
    tone,
    heading = 'p',
    className,
    ...props
}: EmptyStateProps) {
    const Heading = heading;
    return (
        <div
            data-slot="empty-state"
            data-tone={tone ?? 'default'}
            role={tone === 'destructive' ? 'alert' : undefined}
            className={cn(emptyStateVariants({ tone }), className)}
            {...props}
        >
            <span
                data-slot="empty-state-icon"
                aria-hidden
                className="bg-muted text-muted-foreground group-data-[tone=success]/empty:bg-success-muted group-data-[tone=success]/empty:text-success group-data-[tone=destructive]/empty:text-destructive mb-2 flex size-12 items-center justify-center rounded-full group-data-[tone=destructive]/empty:mb-0 [&_svg]:size-[21px]"
            >
                {icon}
            </span>
            <Heading className="text-sm-medium text-foreground">{title}</Heading>
            {body ? (
                <p className="text-muted-foreground max-w-(--layout-measure-empty-state) text-sm group-data-[tone=destructive]/empty:max-w-[420px] group-data-[tone=destructive]/empty:text-xs">
                    {body}
                </p>
            ) : null}
            {actions ? (
                <div className="mt-2 flex items-center gap-2 group-data-[tone=destructive]/empty:mt-1.5">
                    {actions}
                </div>
            ) : null}
        </div>
    );
}

const emptyStateVariants = cva(
    'group/empty flex flex-col items-center gap-2 px-6 py-10 text-center',
    {
        variants: {
            tone: {
                default: '',
                success: '',
                destructive: 'gap-2.5 pt-[52px] pb-14',
            },
        },
        defaultVariants: { tone: 'default' },
    },
);

export { EmptyState, type EmptyStateProps };
